<script lang="ts">
  import type { HudState } from '../hud-state.svelte';
  import type { TouchControlsView, TouchInputSource } from '../../input/touch-source';
  import { STICK_MAX_RADIUS_PX } from '../../input/thumbstick';
  import { touchLayout } from './touch-layout';

  interface Props {
    touch: TouchInputSource;
    hud: HudState;
  }

  const { touch, hud }: Props = $props();

  let view: TouchControlsView = $state({ enabled: false, stick: null, firing: false });
  let zone: HTMLDivElement | undefined = $state();
  let cssWidth = $state(0);
  let cssHeight = $state(0);

  $effect(() => {
    view = touch.snapshot();
    return touch.subscribe(() => {
      view = touch.snapshot();
    });
  });

  $effect(() => (zone === undefined ? undefined : touch.attachStickZone(zone)));

  const live = $derived(
    view.enabled &&
      hud.playing &&
      !hud.dead &&
      !hud.spectating &&
      !hud.evolveScreenOpen &&
      hud.endGame === null,
  );
  $effect(() => {
    if (!live) touch.releaseAll();
  });

  const layout = $derived(touchLayout(cssWidth, cssHeight, hud.cssScale));

  function press(event: PointerEvent, action: () => void): void {
    event.preventDefault();
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    action();
  }
</script>

<div
  class="touch"
  class:touch--live={live}
  bind:clientWidth={cssWidth}
  bind:clientHeight={cssHeight}
>
  <div class="touch__zone" bind:this={zone}></div>

  {#if live && view.stick !== null}
    <div
      class="touch__base"
      style:left="{view.stick.baseX}px"
      style:top="{view.stick.baseY}px"
      style:width="{STICK_MAX_RADIUS_PX * 2}px"
      style:height="{STICK_MAX_RADIUS_PX * 2}px"
    ></div>
    <div
      class="touch__knob"
      class:touch__knob--thrown={view.stick.magnitude > 0}
      style:left="{view.stick.knobX}px"
      style:top="{view.stick.knobY}px"
    ></div>
  {/if}

  {#if live}
    <button
      class="touch__button touch__button--fire"
      class:touch__button--held={view.firing}
      type="button"
      aria-label="Fire"
      style:right="{layout.fire.right}px"
      style:bottom="{layout.fire.bottom}px"
      style:width="{layout.fire.size}px"
      style:height="{layout.fire.size}px"
      onpointerdown={(event) => {
        press(event, () => {
          touch.pressFire();
        });
      }}
      onpointerup={() => {
        touch.releaseFire();
      }}
      onpointercancel={() => {
        touch.releaseFire();
      }}
    >
      Fire
    </button>

    <button
      class="touch__button touch__button--ability"
      type="button"
      aria-label="Use ability"
      style:right="{layout.ability.right}px"
      style:bottom="{layout.ability.bottom}px"
      style:width="{layout.ability.size}px"
      style:height="{layout.ability.size}px"
      onpointerdown={(event) => {
        press(event, () => {
          touch.pressAbility();
        });
      }}
    >
      Skill
    </button>
  {/if}
</div>

<style>
  .touch {
    position: absolute;
    inset: 0;
    pointer-events: none;
    user-select: none;
    -webkit-user-select: none;
    font-family: xirod, sans-serif;
  }

  /* A real element, not a coordinate check: a drag that reached the canvas would read as a click. */
  .touch__zone {
    position: absolute;
    inset: 0 50% 0 0;
    touch-action: none;
  }

  .touch--live .touch__zone,
  .touch--live .touch__button {
    pointer-events: auto;
  }

  .touch__base,
  .touch__knob {
    position: absolute;
    border-radius: 50%;
    transform: translate(-50%, -50%);
  }

  .touch__base {
    border: 2px solid rgb(119 238 255 / 35%);
    background: rgb(4 6 15 / 30%);
  }

  .touch__knob {
    width: 56px;
    height: 56px;
    border: 2px solid rgb(119 238 255 / 70%);
    background: rgb(119 238 255 / 22%);
  }

  .touch__knob--thrown {
    background: rgb(119 238 255 / 38%);
  }

  .touch__button {
    position: absolute;
    /* Added, not max()ed: the HUD band is measured from the viewport edge, below the inset. */
    margin-bottom: env(safe-area-inset-bottom);
    margin-right: env(safe-area-inset-right);
    padding: 0;
    border: 2px solid rgb(119 238 255 / 55%);
    border-radius: 50%;
    background: rgb(16 22 42 / 55%);
    color: #77eeff;
    font: inherit;
    font-size: 13px;
    text-transform: uppercase;
    touch-action: none;
  }

  .touch__button--fire {
    border-color: rgb(255 107 107 / 60%);
    color: #ff9b9b;
  }

  .touch__button--held {
    background: rgb(255 107 107 / 35%);
  }

  .touch__button--ability {
    font-size: 11px;
  }
</style>
